import { useEffect, useMemo, useState } from 'react';
import { AlertOctagon, Minus, Plus, Timer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Textarea } from '@/components/ui/Textarea';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { Label } from '@/components/ui/Label';
import { WorkPhotoCapture } from './WorkPhotoCapture';
import { useBomComponents, useReportDefect, uploadCapture } from '@/features/workMode/hooks';
import { compressImage } from '@/features/workMode/images';
import { describeWorkError, formatClock } from '@/features/workMode/api';
import { SCRAP_TAGS, slugifyScrapTag } from '@/features/scrap/tags';
import {
  DEFECT_DISPOSITIONS,
  DEFECT_SEVERITIES,
  type ScrappedPart,
  type WorkDefectDisposition,
  type WorkDefectSeverity,
  type WorkInstructionStep,
  type WorkSession,
  type WorkSessionSnapshot,
} from '@/features/workMode/types';

interface WorkDefectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: WorkSession;
  steps: WorkInstructionStep[];
  /** Live net seconds already on this unit. Scrap stores this with the log. */
  committedSeconds: number;
  /** Scrap opens the scrap form directly. Defect keeps the other dispositions. */
  mode?: 'defect' | 'scrap';
  onReported?: (snapshot: WorkSessionSnapshot) => void;
}

/**
 * Bench report. Scrap asks for photos, a type tag, whether it needs rework,
 * and which components are thrown out. The time already on the unit is saved
 * with the log — the worker sees that clock before they submit.
 */
export function WorkDefectDialog({
  open,
  onOpenChange,
  session,
  steps,
  committedSeconds,
  mode = 'defect',
  onReported,
}: WorkDefectDialogProps) {
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<WorkDefectSeverity>('minor');
  const [disposition, setDisposition] = useState<WorkDefectDisposition>('scrap');
  const [tags, setTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState('');
  const [needsRework, setNeedsRework] = useState(false);
  const [reworkNotes, setReworkNotes] = useState('');
  const [stepId, setStepId] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(50);
  const [qty, setQty] = useState<Record<string, number>>({});
  const [extraParts, setExtraParts] = useState<ScrappedPart[]>([]);
  const [extraName, setExtraName] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [restart, setRestart] = useState(true);
  const [busy, setBusy] = useState(false);

  const { data: components = [] } = useBomComponents(open ? session.item_id : null);
  const report = useReportDefect();
  const isScrap = mode === 'scrap' || disposition === 'scrap';

  useEffect(() => {
    if (!open) return;
    setDescription('');
    setSeverity(mode === 'scrap' ? 'major' : 'minor');
    setDisposition('scrap');
    setTags([]);
    setCustomTag('');
    setNeedsRework(false);
    setReworkNotes('');
    setStepId(null);
    setProgress(50);
    setQty({});
    setExtraParts([]);
    setExtraName('');
    setPhotos([]);
    setRestart(true);
  }, [open, mode]);

  useEffect(() => {
    if (mode === 'scrap') {
      setRestart(true);
      return;
    }
    setRestart(disposition === 'scrap' || disposition === 'hold');
  }, [disposition, mode]);

  const scrappedParts = useMemo<ScrappedPart[]>(() => {
    const fromBom = components
      .filter((c) => (qty[c.item_id] ?? 0) > 0)
      .map((c) => ({ item_id: c.item_id, sku: c.sku, name: c.name, qty: qty[c.item_id] }));
    return [...fromBom, ...extraParts];
  }, [components, qty, extraParts]);

  const pickStep = (s: WorkInstructionStep) => {
    setStepId((cur) => (cur === s.id ? null : s.id));
    setProgress(Math.round((s.step_number / Math.max(1, steps.length)) * 100));
  };

  const bump = (id: string, d: number) => setQty((q) => ({ ...q, [id]: Math.max(0, (q[id] ?? 0) + d) }));

  const toggleTag = (value: string) => {
    setTags((cur) => (cur.includes(value) ? cur.filter((t) => t !== value) : cur.length >= 8 ? cur : [...cur, value]));
  };

  const addCustomTag = () => {
    const slug = slugifyScrapTag(customTag);
    if (!slug) return;
    setTags((cur) => (cur.includes(slug) || cur.length >= 8 ? cur : [...cur, slug]));
    setCustomTag('');
  };

  const submit = async () => {
    if (description.trim().length === 0) {
      toast.error('Tell us what went wrong — a few words is enough.');
      return;
    }
    if (isScrap && tags.length === 0) {
      toast.error('Tag the type of scrap — pick at least one.');
      return;
    }
    setBusy(true);
    try {
      const paths: string[] = [];
      for (const f of photos) {
        const blob = await compressImage(f);
        paths.push(await uploadCapture(session.organization_id, session.id, blob, f.name.replace(/\.[^.]+$/, '') + '.jpg'));
      }
      const willRestart = mode === 'scrap' ? true : restart;
      const snap = await report.mutateAsync({
        sessionId: session.id,
        description: description.trim(),
        severity,
        disposition: mode === 'scrap' ? 'scrap' : disposition,
        progressPct: progress,
        stepId,
        scrappedParts,
        photoPaths: paths,
        restartUnit: willRestart,
        scrapTags: isScrap ? tags : [],
        needsRework: isScrap ? needsRework : false,
        reworkNotes: isScrap && needsRework ? reworkNotes.trim() || null : null,
      });
      toast.success(
        willRestart
          ? isScrap
            ? 'Scrap logged. The time on this unit was saved and the timer restarted.'
            : 'Defect logged. Timer restarted for a fresh unit.'
          : isScrap
            ? 'Scrap logged. The time already spent was saved.'
            : 'Defect logged.',
      );
      onReported?.(snap);
      onOpenChange(false);
    } catch (e) {
      toast.error(describeWorkError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-2xl p-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            {mode === 'scrap' ? (
              <Trash2 className="h-5 w-5 text-fresh-salmon-dark" />
            ) : (
              <AlertOctagon className="h-5 w-5 text-fresh-salmon-dark" />
            )}
            {mode === 'scrap' ? 'Scrap this unit' : 'Report a defect'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 px-6 py-5">
          {isScrap && (
            <section className="flex items-center gap-4 rounded-xl border border-fresh-salmon/60 bg-fresh-salmon/15 px-4 py-3">
              <Timer className="h-5 w-5 shrink-0 text-fresh-salmon-dark" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">Time already on this part</p>
              </div>
              <p className="text-3xl font-light tabular-nums tracking-tight text-foreground">{formatClock(committedSeconds)}</p>
            </section>
          )}

          {mode === 'scrap' && (
            <PhotoSection photos={photos} onChange={setPhotos} disabled={busy} prompt />
          )}

          <section className="space-y-2">
            <Label className="text-base">{mode === 'scrap' ? 'What went wrong?' : 'What happened?'}</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Cracked boss on the left mount after torquing the M6 bolt."
              className="min-h-[88px] text-base"
              autoFocus={mode !== 'scrap'}
            />
          </section>

          {isScrap && (
            <section className="space-y-2">
              <Label className="text-base">Type of scrap</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {SCRAP_TAGS.map((t) => (
                  <Chip key={t.value} active={tags.includes(t.value)} onClick={() => toggleTag(t.value)} title={t.label} />
                ))}
              </div>
              {tags.some((t) => !SCRAP_TAGS.some((p) => p.value === t)) && (
                <div className="flex flex-wrap gap-1.5">
                  {tags
                    .filter((t) => !SCRAP_TAGS.some((p) => p.value === t))
                    .map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => toggleTag(t)}
                        className="rounded-full border border-info bg-info-soft/30 px-2.5 py-1 text-xs text-info"
                      >
                        {t.replace(/_/g, ' ')} · remove
                      </button>
                    ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={customTag}
                  onChange={(e) => setCustomTag(e.target.value)}
                  placeholder="Other type"
                  className="h-11"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomTag();
                    }
                  }}
                />
                <Button type="button" variant="outline" className="h-11" disabled={!slugifyScrapTag(customTag)} onClick={addCustomTag}>
                  Add tag
                </Button>
              </div>
            </section>
          )}

          {mode === 'defect' && (
            <section className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-base">How bad is it?</Label>
                <div className="grid grid-cols-3 gap-2">
                  {DEFECT_SEVERITIES.map((s) => (
                    <Chip
                      key={s.value}
                      active={severity === s.value}
                      onClick={() => setSeverity(s.value)}
                      title={s.label}
                      tone={s.value === 'critical' ? 'danger' : 'default'}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-base">What happens to it?</Label>
                <div className="grid grid-cols-2 gap-2">
                  {DEFECT_DISPOSITIONS.map((d) => (
                    <Chip key={d.value} active={disposition === d.value} onClick={() => setDisposition(d.value)} title={d.label} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {mode === 'scrap' && (
            <section className="space-y-2">
              <Label className="text-base">How bad is it?</Label>
              <div className="grid grid-cols-3 gap-2">
                {DEFECT_SEVERITIES.map((s) => (
                  <Chip
                    key={s.value}
                    active={severity === s.value}
                    onClick={() => setSeverity(s.value)}
                    title={s.label}
                    tone={s.value === 'critical' ? 'danger' : 'default'}
                  />
                ))}
              </div>
            </section>
          )}

          {isScrap && (
            <section className="space-y-3 rounded-xl border border-border bg-secondary/40 px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground">Needs rework?</p>
                </div>
                <Switch checked={needsRework} onCheckedChange={setNeedsRework} aria-label="Needs rework" />
              </div>
              {needsRework && (
                <Textarea
                  value={reworkNotes}
                  onChange={(e) => setReworkNotes(e.target.value)}
                  placeholder="What should be reworked?"
                  className="min-h-[72px] text-base"
                />
              )}
            </section>
          )}

          <section className="space-y-2">
            <Label className="text-base">How far along were you?</Label>
            {steps.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {steps.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => pickStep(s)}
                    className={cn(
                      'min-h-[44px] rounded-xl border px-3 py-2 text-left text-sm transition-colors',
                      stepId === s.id ? 'border-info bg-info-soft/30 text-info' : 'border-border bg-card text-foreground hover:bg-secondary',
                    )}
                  >
                    <span className="font-medium">Step {s.step_number}</span>
                    <span className="ml-1.5 text-muted-foreground">{s.title}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-4">
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={progress}
                onChange={(e) => setProgress(Number(e.target.value))}
                className="h-2 flex-1 accent-[#0E415E]"
                aria-label="Percent complete"
              />
              <span className="w-14 text-right text-lg font-medium text-foreground">{progress}%</span>
            </div>
          </section>

          <section className="space-y-2">
            <Label className="text-base">{isScrap ? 'Components thrown out' : 'Which parts are scrap?'}</Label>
            {components.length === 0 && extraParts.length === 0 && (
              <p className="text-sm text-muted-foreground">No bill of materials for this item.</p>
            )}
            <ul className="divide-y divide-border rounded-xl border border-border">
              {components.map((c) => (
                <li key={c.item_id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{c.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {c.sku} · {c.quantity} {c.uom} per unit
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="icon" className="h-11 w-11" onClick={() => bump(c.item_id, -1)} disabled={(qty[c.item_id] ?? 0) === 0} aria-label={`Fewer ${c.name}`}>
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center text-lg font-medium tabular-nums">{qty[c.item_id] ?? 0}</span>
                    <Button type="button" variant="outline" size="icon" className="h-11 w-11" onClick={() => bump(c.item_id, 1)} aria-label={`More ${c.name}`}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
              {extraParts.map((p, i) => (
                <li key={`${p.name}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2">
                  <p className="text-sm font-medium text-foreground">{p.name}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">× {p.qty}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setExtraParts((l) => l.filter((_, j) => j !== i))}>
                      Remove
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <Input
                value={extraName}
                onChange={(e) => setExtraName(e.target.value)}
                placeholder="Other part (name or SKU)"
                className="h-11"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && extraName.trim()) {
                    e.preventDefault();
                    setExtraParts((l) => [...l, { item_id: null, sku: '', name: extraName.trim(), qty: 1 }]);
                    setExtraName('');
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="h-11"
                disabled={!extraName.trim()}
                onClick={() => {
                  setExtraParts((l) => [...l, { item_id: null, sku: '', name: extraName.trim(), qty: 1 }]);
                  setExtraName('');
                }}
              >
                Add
              </Button>
            </div>
          </section>

          {mode !== 'scrap' && <PhotoSection photos={photos} onChange={setPhotos} disabled={busy} />}

          {mode !== 'scrap' && (
            <section className="flex items-center justify-between gap-4 rounded-xl border border-border bg-secondary/40 px-4 py-3">
              <p className="text-sm font-medium text-foreground">Start this unit over</p>
              <Switch checked={restart} onCheckedChange={setRestart} aria-label="Start this unit over" />
            </section>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
          <Button variant="ghost" size="lg" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button size="lg" variant="destructive" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : mode === 'scrap' ? 'Log scrap' : 'Log defect'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PhotoSection({
  photos,
  onChange,
  disabled,
  prompt = false,
}: {
  photos: File[];
  onChange: (files: File[]) => void;
  disabled: boolean;
  prompt?: boolean;
}) {
  return (
    <section className="space-y-2">
      <Label className="text-base">{prompt ? 'Take pictures of the scrap' : 'Photos'}</Label>
      <WorkPhotoCapture files={photos} onChange={onChange} disabled={disabled} />
    </section>
  );
}

function Chip({
  active,
  onClick,
  title,
  tone = 'default',
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  tone?: 'default' | 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex min-h-[56px] items-center justify-center rounded-xl border px-3 py-2 text-center transition-colors',
        active
          ? tone === 'danger'
            ? 'border-fresh-salmon-dark bg-fresh-salmon/25 text-fresh-salmon-dark'
            : 'border-info bg-info-soft/30 text-info'
          : 'border-border bg-card text-foreground hover:bg-secondary',
      )}
    >
      <span className="text-sm font-medium">{title}</span>
    </button>
  );
}
